// "Create organization" modal — single unified component used by BOTH:
//   • AppWorkspaceSwitcher's "+ Create organization" footer button
//     (workspace-project/components/AppWorkspaceSwitcher.js)
//   • Canvas dashboard sidebar's "+ New organization" button
//     (canvas/pages/dashboard.js — NewOrgButton)
//
// Replaces the previous split: a simple variant here + a feature-rich
// inline variant (NewOrgModal) inside dashboard.js. The dashboard path
// already had live slug-availability checking + auto-suggested suffix
// + one-click "Use <slug-N>" recovery on conflict; the switcher path
// trusted the user and let the POST be the final arbiter. The two
// flows were never structurally different — just two evolutionary
// stages — so we keep the better UX (live check) and apply it to
// both call sites.
//
// State key namespace: `_createOrg*` (matches the existing stateKey
// the switcher already writes). The `newOrg*` namespace previously
// used by dashboard.js is retired; dashboard's NewOrgButton now writes
// the same `_createOrgModalOpen` flag.
//
// Mount: shell-root (workspace/app.js shellApp.AppCreateOrgModal) so
// the modal renders regardless of which sub-app's page is active. The
// previous mount inside workspace-project/app.js never rendered
// because smbls 3.14's auto-router mounts pages directly into Slot —
// the wp wrapper never instantiates.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

// Local slug helper. Intentionally differs from
// shared/functions/slugify.js: no length truncation (org slugs allow
// up to 60 chars server-side) and no random `slot-xxxx` fallback —
// empty input must stay empty so the live availability check no-ops.
const _slugifyLocal = (name) =>
  (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Module-level debounce timer so rapid name input coalesces into one
// /organizations?slug=X check. Resets on every keystroke.
let _slugDebounceTimer = null

// Returns true = available, false = taken, null = check failed.
// Routes through `sdk.checkOrganizationSlug` so the call honors the
// channel-resolved workspaceApiBase + SDK auth header.
const _checkSlugAvailable = async (slug) => {
  if (!slug) return null
  try {
    return await shellGetSDK().checkOrganizationSlug(slug)
  } catch {
    return null
  }
}

// Find the first available slug: tries base, then base-2, base-3 …
// up to N. Returns null on full exhaustion (vanishingly rare with
// N=20).
const _findAvailableSlug = async (base, maxAttempts = 20) => {
  for (let n = 0; n < maxAttempts; n++) {
    const candidate = n === 0 ? base : `${base}-${n + 1}`
    if ((await _checkSlugAvailable(candidate)) === true) return candidate
  }
  return null
}

// Debounced live slug check. On taken, auto-advances to the first
// free -2/-3/-N suffix and surfaces it as `_createOrgSlugSuggestion`
// so the UI can show a one-click "Use <slug>" button.
const _scheduleSlugCheck = (rawSlug, root) => {
  if (_slugDebounceTimer) clearTimeout(_slugDebounceTimer)
  const base = _slugifyLocal(rawSlug)
  if (!base) {
    root.update({
      _createOrgSlug: '',
      _createOrgSlugChecking: false,
      _createOrgSlugStatus: null,
      _createOrgSlugSuggestion: null
    })
    return
  }
  root.update({ _createOrgSlugChecking: true, _createOrgSlugStatus: null })
  _slugDebounceTimer = setTimeout(async () => {
    _slugDebounceTimer = null
    const available = await _checkSlugAvailable(base)
    if (available === true) {
      root.update({
        _createOrgSlug: base,
        _createOrgSlugChecking: false,
        _createOrgSlugStatus: 'available',
        _createOrgSlugSuggestion: null
      })
    } else if (available === false) {
      const suggestion = await _findAvailableSlug(base)
      root.update({
        _createOrgSlug: suggestion || base,
        _createOrgSlugChecking: false,
        _createOrgSlugStatus: suggestion ? 'suggested' : 'taken',
        _createOrgSlugSuggestion: suggestion
      })
    } else {
      // Network error — let the user proceed; server is the final arbiter.
      root.update({ _createOrgSlugChecking: false, _createOrgSlugStatus: 'error' })
    }
  }, 350)
}

// Submit handler — POSTs through sdk.createOrganization, handles
// race-condition 409 (slug taken between live-check and POST) by
// surfacing the auto-suffix suggestion, refreshes JWT claims so the
// new org appears in `_orgs(root)`, then hard-reloads so every
// `method: 'select'` query re-keys under the new active org. Same
// recovery convention as the NoOrgOverlay first-time setup.
// `el` is the form element: every refusal below is a bundle sentence
// resolved through it, and a server's own message passes through as-is.
const _submitCreate = async (root, el) => {
  const name = (root?._createOrgName || '').trim()
  const slug = (root?._createOrgSlug || _slugifyLocal(name)).trim()
  if (!name || !slug) {
    root.update({ _createOrgError: el.call('polyglot', 'workspace.createOrg.errorRequired') })
    return
  }
  root.update({
    _createOrgBusy: true,
    _createOrgError: null,
    _createOrgSlugConflict: null
  })
  try {
    const created = await shellGetSDK().createOrganization({ name, slug, type: 'team' })
    // Make the brand-new org the ACTIVE one before reloading. The server's
    // createOrganization creates the org + its HQ workspace but does NOT move
    // the creator's persisted `activeOrganization`/`activeWorkspace` — so
    // without this, `getMe`/claims still resolve the OLD org after the reload.
    // The user lands back in their previous org, and because the new org and
    // the old one then collapse onto the same `workspace_org_id`, a later
    // switch between them computes `_orgChanged === false` and takes the no-op
    // `switchWorkspace` branch — the "create an org, then can't switch
    // org / stuck" bug (#2347). switchOrg persists the active org in Mongo AND
    // federates the workspace, so the post-reload claims resolve the new org.
    const newOrgId = created?.organization?._id || created?.organization?.id || null
    const newHomeWorkspaceId = created?.workspace?._id || created?.workspace?.id || null
    if (newOrgId) {
      try {
        await shellGetSDK().switchOrg(newOrgId, { homeWorkspaceId: newHomeWorkspaceId || undefined })
      } catch (switchErr) {
        // Non-fatal: the org was created. Fall back to a claims refresh so it at
        // least appears in the list; the user can switch into it manually.
        console.warn(
          '[AppCreateOrgModal] activate new org failed:',
          switchErr?.message || switchErr
        )
      }
    }
    // Active org is persisted in Mongo by switchOrg above; the hard reload
    // below re-derives claims from Mongo via getMe (no gov refreshClaims hop).
    if (typeof window !== 'undefined') window.location.href = '/'
  } catch (err) {
    const msg = err?.message || ''
    const isSlugTaken =
      msg === 'slug_taken' ||
      msg.includes('slug_taken') ||
      (msg.toLowerCase().includes('slug') && msg.toLowerCase().includes('taken'))
    if (isSlugTaken) {
      const suggestion = await _findAvailableSlug(slug)
      root.update({
        _createOrgBusy: false,
        _createOrgError: el
          .call('polyglot', 'workspace.createOrg.errorSlugTaken')
          .replace('{slug}', slug),
        _createOrgSlugConflict: slug,
        _createOrgSlug: suggestion || slug,
        _createOrgSlugSuggestion: suggestion,
        _createOrgSlugStatus: suggestion ? 'suggested' : 'taken'
      })
      return
    }
    root.update({
      _createOrgBusy: false,
      _createOrgError: err?.message || el.call('polyglot', 'workspace.createOrg.errorFailed')
    })
  }
}

// Body-only component — backdrop, ESC binding, dismiss-on-backdrop are
// owned by @symbo.ls/windows ModalLayer (renders inside CustomTemplate
// Slot). Triggered via `el.call('openCreateOrgModal')` and closed via
// `el.call('closeCreateOrgModal')` — helpers in workspace/functions/.
// The helpers also handle the field reset that used to live in
// `extraClose`. All `_createOrg*` state fields stay as data carriers.
export const AppCreateOrgModal = {
  // Fill the @symbo.ls/windows custom-template modal slot rather than forcing a
  // fixed 'H' (524px) width — the slot is ~466px, so the fixed width overflowed
  // the card and pushed the inputs/buttons past its right edge (the "inputs
  // overlapping" bug). 100% conforms to whatever width the slot provides.
  width: '100%',
  maxWidth: '90vw',
  flow: 'y',
  gap: 'A',
  // MODAL-X-OVERFLOW-1: @symbo.ls/windows' ModalLayer.js deliberately sets
  // `overflow:'visible'` + a null-default `maxHeight` on the outer
  // ModalWindow chrome (2026-06-03, to stop the rounded-corner clip eating
  // the Close X — see that file's comment) — so a custom-template body gets
  // NO scroll containment unless it caps its own height. Without this, tall
  // content (or a short viewport) pushes the box past the top of the
  // screen, taking ModalHeader — and its X — off-screen with it: reads as
  // "lost the close button" even though CloseButton itself renders fine.
  // Capping the ROOT directly (not relying on ancestor flex-shrink through
  // the vendored CustomTemplate/Slot chain, which this component doesn't
  // own) guarantees containment regardless of viewport height. Paired with
  // openCreateOrgModal's own `maxHeight` ceiling (workspace/functions/
  // createOrgModal.js) so the two agree.
  maxHeight: '80vh',
  // No `overflow` here: the Form's Fields region is the one scroller (below),
  // and a clip on this root cut the brand ModalHeader's Close X — pulled into
  // the window's padding by a negative margin — by 13px (live on dev
  // 2026-09-26). `minHeight: 0` lets the column shrink to the cap.
  minHeight: '0',

  // Brand ModalWindow always renders a ModalHeader with H + P
  // placeholder slots ('Heading' / 'Paragraph'). Populate them
  // instead of mounting a custom Title + Hint sibling — otherwise
  // both render and the modal shows leftover 'Heading / Paragraph'
  // text above the real content (same bug as AgentModal in
  // canvas/pages/team-and-agents.js).
  ModalHeader: {
    Hgroup: {
      H: { text: '{{ workspace.createOrg.title | polyglot }}' },
      P: {
        text: '{{ workspace.createOrg.subtitle | polyglot }}'
      }
    },
    // Override the brand's inherited `Icons.Close.onClick` (which
    // dispatches the stateKey-walker closeModal — would no-op now
    // that this component lost its stateKey).
    Icons: {
      Close: {
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('closeCreateOrgModal')
        }
      }
    }
  },

  Form: {
    tag: 'form',
    flow: 'y',
    gap: 'Z1',
    novalidate: true,
    // Header/footer pin, fields scroll (canonical shape — see
    // ChannelSettingsModal.js's Header/Content/Footer split): Form itself
    // shrinks to whatever height ROOT's `maxHeight` leaves it, and Fields
    // is the ONLY region that scrolls (`flex:1, minHeight:0, overflow:
    // auto`) — Actions stays pinned below it instead of scrolling away
    // with the inputs.
    minHeight: '0',
    flex: '1',

    onSubmit: (e, el, s) => {
      e.preventDefault()
      if (s.root?._createOrgBusy) return
      _submitCreate(s.root, el)
    },

    Fields: {
      flow: 'y',
      gap: 'Z1',
      flex: '1',
      minHeight: '0',
      overflowY: 'auto',

      // The shared field kit (shared/components/AppField.js): AppField owns
      // the caption label, AppInput the control surface (the brand `field`
      // fill, control scale, hairline edge, global focus ring) — this form
      // keeps the behaviour only. `label` is a FUNCTION prop: an attribute
      // never resolves a `{{ }}` template.
      NameField: {
        extends: 'AppField',
        label: (el) => el.call('polyglot', 'workspace.createOrg.nameLabel'),

        AppInput: {
          type: 'text',
          name: 'name',
          // A company NAME, not a shape the field enforces — a Georgian
          // visitor gets a Georgian example. Its SLUG sibling below stays
          // literal: that field only accepts ASCII.
          placeholder: (el) => el.call('polyglot', 'workspace.createOrg.namePlaceholder'),
          // The kit's own `ariaLabel` key, so ONE effect owns aria-label.
          ariaLabel: (el) => el.call('polyglot', 'workspace.createOrg.nameLabel'),
          autofocus: true,
          required: true,
          maxlength: 60,
          value: (el, s) => s.root?._createOrgName || '',
          // `autofocus` doesn't fire on a dynamic (modal) mount — focus the field
          // once on render so keyboard + screen-reader users land inside the
          // dialog instead of on the page behind it. (a11y, 2026-06-17)
          // After the modal root's own focus move (one frame), so the field —
          // not the header's Close — holds the keyboard.
          onRender: (el) => {
            if (el.__autoFocused) return
            el.__autoFocused = true
            setTimeout(() => el.call('focusCreateOrgName', el), 60)
          },
          // The onboarding gate can open this dialog under the boot screen's
          // `inert` lock, where the focus above is a no-op (persona audit
          // WL-9): ask again the moment the lock lifts.
          'onWindowSmbls:boot-ready': (e, el) => el.call('focusCreateOrgName', el),
          onInput: (e, el, s) => {
            const name = e.target.value
            const wasSlugDirty = !!s.root?._createOrgSlugDirty
            const patch = {
              _createOrgName: name,
              _createOrgError: null,
              _createOrgSlugConflict: null
            }
            if (!wasSlugDirty) {
              patch._createOrgSlug = _slugifyLocal(name)
              patch._createOrgSlugDirty = false
            }
            // `_createOrgSlug` in the patch flows to the slug Input's reactive
            // `value:` factory, so the field re-renders with the derived slug
            // automatically — no DOM querySelector/value write needed.
            s.root.update(patch)
            if (!wasSlugDirty) _scheduleSlugCheck(name, s.root)
          }
        }
      },

      // Workspace URL — the slug auto-generates from the name and shows as a
      // live `my.symbols.app/<slug>` preview (per Nika 2026-06-17). The slug
      // stays inline-editable for conflict recovery; status + auto-adjust ride
      // the same _createOrgSlug* logic.
      //
      // The kit's prefix field (shared/components/AppField.js AppFieldPrefix):
      // the caption, then ONE field surface that holds the fixed host and the
      // slug input — one edge, one hover step, one focus ring, the same
      // control scale as the name field above. A click on the caption or on
      // the host focuses the slug. A taken slug takes the kit's danger edge
      // (`hasError` on the group, a class rule — never a reactive border
      // factory); the status word at the end of the row carries the verdict in
      // its own ink. An available slug keeps the plain edge: the word says it.
      UrlField: {
        extends: 'AppFieldPrefix',
        label: (el) => el.call('polyglot', 'workspace.createOrg.urlLabel'),
        prefixText: 'my.symbols.app/',

        AppInputPrefix: {
          class: {
            hasError: (el, s) => s.root?._createOrgSlugStatus === 'taken'
          },

          AppInputBare: {
            name: '_createOrgSlug',
            type: 'text',
            placeholder: 'acme',
            maxlength: 60,
            // The kit's own key, so ONE effect owns aria-label. The slug's own
            // name ("Workspace URL slug") is more exact than the caption.
            ariaLabel: (el) => el.call('polyglot', 'workspace.createOrg.slugAria'),
            // Reactive controlled value — the auto-derived slug (and any
            // auto-adjusted / suggested slug) flows straight from root state into
            // the field. `value:` is a framework-reactive prop, so this replaces
            // the old onRender el.node.value DOM write (FA511/FA501).
            value: (el, s) => s.root?._createOrgSlug || '',
            onInput: (e, el, s) => {
              const val = _slugifyLocal(e.target.value)
              e.target.value = val
              s.root.update({
                _createOrgSlug: val,
                _createOrgSlugDirty: true,
                _createOrgError: null,
                _createOrgSlugConflict: null
              })
              _scheduleSlugCheck(val, s.root)
            }
          },

          SlugStatus: {
            tag: 'span',
            fontSize: 'Y2',
            whiteSpace: 'nowrap',
            // The group sets the host flush against the slug (gap 0); the
            // status word keeps its own space from the typed text.
            margin: '- - - Z',
            'aria-live': 'polite',
            hide: (el, s) => !s.root?._createOrgSlug,
            // Semantic ink pairs (Rule 69): a raw hue fails its contrast floor
            // in one of the two schemes.
            color: (el, s) => {
              if (s.root?._createOrgSlugChecking) return 'caption'
              const st = s.root?._createOrgSlugStatus
              if (st === 'available') return 'successInk'
              if (st === 'taken') return 'dangerInk'
              if (st === 'suggested') return 'warningInk'
              return 'caption'
            },
            text: (el, s) => {
              if (s.root?._createOrgSlugChecking)
                return el.call('polyglot', 'workspace.createOrg.slugStatusChecking')
              const st = s.root?._createOrgSlugStatus
              if (st === 'available')
                return el.call('polyglot', 'workspace.createOrg.slugStatusAvailable')
              if (st === 'taken') return el.call('polyglot', 'workspace.createOrg.slugStatusTaken')
              if (st === 'suggested')
                return el.call('polyglot', 'workspace.createOrg.slugStatusAdjusted')
              return ''
            }
          }
        },

        SlugHint: {
          hide: (el, s) => {
            const st = s.root?._createOrgSlugStatus
            const conflict = s.root?._createOrgSlugConflict
            return st !== 'suggested' && st !== 'taken' && !conflict
          },
          flow: 'x',
          align: 'center left',
          gap: 'Z',
          margin: 'V 0 0',

          HintText: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            flex: '1',
            text: (el, s) => {
              const sug = s.root?._createOrgSlugSuggestion
              const conflict = s.root?._createOrgSlugConflict
              if (conflict && sug)
                return el
                  .call('polyglot', 'workspace.createOrg.slugTakenTry')
                  .replace('{slug}', conflict)
                  .replace('{suggestion}', sug)
              if (conflict)
                return el
                  .call('polyglot', 'workspace.createOrg.slugTakenEdit')
                  .replace('{slug}', conflict)
              if (sug)
                return el
                  .call('polyglot', 'workspace.createOrg.slugAdjusted')
                  .replace('{suggestion}', sug)
              return el.call('polyglot', 'workspace.createOrg.slugTaken')
            }
          },

          // Reuse the house Button (secondary theme) instead of a bespoke
          // styled `tag:'button'` — the previous hand-rolled version had a no-op
          // `:hover` (overlay → overlay, no change) and duplicated padding/round/
          // border styling. `display: null` is paired with `hide` because brand
          // Button ships a static inline `display:inline-flex` that a bare
          // `hide` (display:none) loses to. (Rule 63 reuse-first.)
          // The kit's tonal capsule in its compact step (controlHCompact, the
          // kit's own inset) — was the brand Button with a hand-set scale and
          // a `display: null` to let `hide` beat its inline display.
          UseSlugBtn: {
            extends: 'AppCapsuleBtnToolbar',
            compact: true,
            hide: (el, s) => !s.root?._createOrgSlugSuggestion,
            text: (el, s) => {
              const sug = s.root?._createOrgSlugSuggestion
              return sug
                ? el.call('polyglot', 'workspace.createOrg.useSlug').replace('{slug}', sug)
                : ''
            },
            onClick: (e, el, s) => {
              e.preventDefault()
              const sug = s.root?._createOrgSlugSuggestion
              if (!sug) return
              // Updating `_createOrgSlug` re-renders the slug Input via its
              // reactive `value:` factory — no DOM querySelector/value write.
              s.root.update({
                _createOrgSlug: sug,
                _createOrgSlugDirty: true,
                _createOrgSlugStatus: 'available',
                _createOrgSlugSuggestion: null,
                _createOrgSlugConflict: null,
                _createOrgError: null
              })
            }
          }
        }
      },

      Error: {
        hide: (el, s) => !s.root?._createOrgError,
        tag: 'p',
        role: 'alert',
        'aria-live': 'assertive',
        // The AA-safe danger pair (the raw brand `red` read under 4.5:1 as
        // text in light).
        color: 'dangerInk',
        fontSize: 'Y2',
        margin: 'Z 0 0',
        text: (el, s) => s.root?._createOrgError || ''
      }
    }, // Fields — end of the scrolling region

    Actions: {
      flow: 'x',
      gap: 'Z1',
      margin: 'A 0 0',
      justifyContent: 'flex-end',
      align: 'center right',

      // Onboarding-only escape hatch (NoOrgOverlay replacement): keep using
      // the personal org instead of creating a team workspace. Hidden in the
      // normal (switcher) flow, where Cancel is shown instead. Per Nika
      // 2026-06-17 "add one more option 'Use personal workspace'".
      // The kit footer (DESIGN_SPEC §6.9, L68): the quiet actions are ghost
      // capsules, the primary is the ink capsule — the kit owns the scale, the
      // washes and the disabled step (was the brand Button with a hand-set
      // scale and a `display: null` to let `hide` beat its inline display).
      UsePersonalBtn: {
        extends: 'AppCapsuleBtnGhost',
        hide: (el, s) => !s.root?._createOrgOnboarding,
        disabled: (el, s) => !!s.root?._createOrgBusy,
        text: '{{ workspace.createOrg.usePersonal | polyglot }}',
        onClick: (e, el) => {
          e.preventDefault()
          el.call('usePersonalWorkspace')
        }
      },

      CancelBtn: {
        extends: 'AppCapsuleBtnGhost',
        hide: (el, s) => !!s.root?._createOrgOnboarding,
        disabled: (el, s) => !!s.root?._createOrgBusy,
        text: '{{ workspace.createOrg.cancel | polyglot }}',
        onClick: (e, el) => {
          e.preventDefault()
          // closeCreateOrgModal also handles the field reset that used
          // to live inline here + in `extraClose`.
          el.call('closeCreateOrgModal')
        }
      },

      SubmitBtn: {
        extends: 'AppCapsuleBtnPrimary',
        type: 'submit',
        // The house floor for a primary whose label changes while it works.
        minWidth: 'E',
        // Disabled while a slug check is in flight so the user can't
        // race-submit before the server's verdict lands. Also
        // disabled when slug is known-taken (no point hitting POST).
        disabled: (el, s) =>
          !!s.root?._createOrgBusy ||
          !!s.root?._createOrgSlugChecking ||
          s.root?._createOrgSlugStatus === 'taken' ||
          !((s.root?._createOrgName || '').trim() && (s.root?._createOrgSlug || '').trim()),
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?._createOrgBusy ? 'workspace.createOrg.submitting' : 'workspace.createOrg.submit'
          )
      }
    }
  }
}
